import React from "react"
import Column from "@amzn/meridian/column"
import Heading from "@amzn/meridian/heading"
import Text from "@amzn/meridian/text"
import Button from "@amzn/meridian/button"
import Alert from "@amzn/meridian/alert"
import { reportClientError } from "src/services/errorReporter"

interface Props { children: React.ReactNode }
interface State { hasError: boolean; error: Error | null }

/**
 * Top-level error boundary. Catches unhandled React render errors,
 * reports them to the Eclipse error logging endpoint, and shows a
 * user-friendly fallback UI.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reportClientError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      source: "ErrorBoundary",
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Column spacingInset="500" spacing="400" alignmentHorizontal="center">
          <Heading level={1} type="d100">Something went wrong</Heading>
          <Alert type="error">
            {this.state.error?.message || "An unexpected error occurred."}
          </Alert>
          <Text type="b200" color="secondary">
            Try refreshing the page. If the problem persists, contact support.
          </Text>
          <Button onClick={() => window.location.reload()}>Refresh Page</Button>
        </Column>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
